import { useTranslation } from 'react-i18next';
import type { SelectedVerse } from '@/components/ShareVersePicker';
import type { UsAddressInput } from '@/lib/us-address-validate';
import styles from './MailCardConfirmReview.module.css';

interface MailCardConfirmReviewProps {
  selectedVerse: SelectedVerse | null;
  includeCardLabel: boolean;
  /** Stripped cardLabel noun, or null when absent. Drives the
   *  not-available / included / omitted row copy. */
  cardLabelNoun: string | null;
  recipientName: string;
  recipientAddress: UsAddressInput;
  senderName: string;
  senderAddress: UsAddressInput;
  onEditVerse: () => void;
  onEditCardLabel: () => void;
  onEditRecipient: () => void;
  onEditSender: () => void;
  /** Fires the parent's redacted onContinue stub (preview-only;
   *  Phase 3 will swap to a real /api/commerce/checkout call). */
  onSend: () => void;
}

/**
 * Confirm review surface — final review before commit
 * (known-sender / show-my-name; the only mode).
 *
 * 4 rows (verse + cardLabel + recipient + sender) each with a
 * per-row Edit affordance. Copy uses the transitional keys
 * (confirmTitleFinishPreview / confirmShowMyNameHelperFinishPreview
 * / confirmFinishPreviewBtn). Phase 3 swaps these to the
 * Phase-1-shipped honest keys (confirmTitle / confirmShowMyNameHelper
 * / confirmSendBtn) AND wires the real submit.
 *
 * No-cardLabel prayers: the parent gates Q2 entirely when
 * cardLabelNoun is null, so the card-label row renders the
 * confirmCardLabelNotAvailable copy explaining why the toggle
 * never appeared.
 */
export default function MailCardConfirmReview({
  selectedVerse,
  includeCardLabel,
  cardLabelNoun,
  recipientName,
  recipientAddress,
  senderName,
  senderAddress,
  onEditVerse,
  onEditCardLabel,
  onEditRecipient,
  onEditSender,
  onSend,
}: MailCardConfirmReviewProps) {
  const { t } = useTranslation();

  const trimmedRecipient = recipientName.trim();

  const ariaForField = (fieldLabel: string) =>
    t('mailCard.confirmEditAriaLabel', { field: fieldLabel });
  const editLabel = t('mailCard.confirmEditLabel');
  const recipientFieldLabel = t('mailCard.recipientPrompt');

  const helperLine = t(
    'mailCard.confirmShowMyNameHelperFinishPreview',
    { recipient: trimmedRecipient },
  );

  const hasCardLabel = cardLabelNoun !== null;
  const cardLabelRowValue = !hasCardLabel
    ? t('mailCard.confirmCardLabelNotAvailable')
    : includeCardLabel
      ? t('mailCard.confirmCardLabelIncluded')
      : t('mailCard.confirmCardLabelOmitted');

  const verseFieldLabel = t('mailCard.previewInsidePage2Label');
  const cardLabelFieldLabel = t('mailCard.cardLabelPrompt');
  const senderFieldLabel = t('mailCard.senderPrompt');

  return (
    <div className={styles.review}>
      <h2 className={styles.heading}>
        {t('mailCard.confirmTitleFinishPreview')}
      </h2>
      <p className={styles.helper}>{helperLine}</p>

      <ul className={styles.rowList}>
        <li className={styles.row}>
          <div className={styles.rowContent}>
            {selectedVerse ? (
              <>
                <span className={styles.rowValuePrimary}>
                  {selectedVerse.text}
                </span>
                <span className={styles.rowValueSecondary}>
                  {selectedVerse.reference}
                </span>
              </>
            ) : (
              <span className={styles.rowValueDim}>
                {t('mailCard.confirmNoVerse')}
              </span>
            )}
          </div>
          <button
            type="button"
            className={styles.editBtn}
            onClick={onEditVerse}
            aria-label={ariaForField(verseFieldLabel)}
          >
            {editLabel}
          </button>
        </li>

        <li className={styles.row}>
          <div className={styles.rowContent}>
            <span className={styles.rowValueDim}>{cardLabelRowValue}</span>
          </div>
          <button
            type="button"
            className={styles.editBtn}
            onClick={onEditCardLabel}
            aria-label={ariaForField(cardLabelFieldLabel)}
          >
            {editLabel}
          </button>
        </li>

        <li className={styles.row}>
          <div className={styles.rowContent}>
            <span className={styles.rowValuePrimary}>{trimmedRecipient}</span>
            <AddressLines address={recipientAddress} />
          </div>
          <button
            type="button"
            className={styles.editBtn}
            onClick={onEditRecipient}
            aria-label={ariaForField(recipientFieldLabel)}
          >
            {editLabel}
          </button>
        </li>

        <li className={styles.row}>
          <div className={styles.rowContent}>
            <span className={styles.rowValuePrimary}>{senderName.trim()}</span>
            <AddressLines address={senderAddress} />
          </div>
          <button
            type="button"
            className={styles.editBtn}
            onClick={onEditSender}
            aria-label={ariaForField(senderFieldLabel)}
          >
            {editLabel}
          </button>
        </li>
      </ul>

      <button type="button" className={styles.sendBtn} onClick={onSend}>
        {t('mailCard.confirmFinishPreviewBtn')}
      </button>
    </div>
  );
}

function AddressLines({ address }: { address: UsAddressInput }) {
  const line2 = address.line2.trim();
  return (
    <>
      <span className={styles.rowValueSecondary}>{address.line1}</span>
      {line2 && (
        <span className={styles.rowValueSecondary}>{line2}</span>
      )}
      <span className={styles.rowValueSecondary}>
        {address.city}, {address.region.toUpperCase()} {address.postalCode}
      </span>
    </>
  );
}
